import React, { useEffect, useState } from "react";
import { FaUsers, FaShippingFast, FaDollarSign, FaStar } from "react-icons/fa";

const stats = [
  {
    id: 1,
    icon: <FaUsers size={28} />,
    title: "Happy Customers",
    value: "12,345+",
    description: "Thousands of happy customers worldwide trust us.",
    bgColor: "from-blue-400 to-blue-600",
    barValue: 85,
  },
  {
    id: 2,
    icon: <FaShippingFast size={28} />,
    title: "Orders Delivered",
    value: "98,765+",
    description: "Fast and reliable delivery at your doorstep.",
    bgColor: "from-green-400 to-green-600",
    barValue: 75,
  },
  {
    id: 3,
    icon: <FaDollarSign size={28} />,
    title: "Revenue Generated",
    value: "$1.2M+",
    description: "Millions of dollars in transactions processed securely.",
    bgColor: "from-yellow-400 to-yellow-600",
    barValue: 90,
  },
  {
    id: 4,
    icon: <FaStar size={28} />,
    title: "5-Star Ratings",
    value: "4,500+",
    description: "Consistently top-rated by our satisfied customers.",
    bgColor: "from-pink-400 to-pink-600",
    barValue: 80,
  },
];

const AdvancedStats = () => {
  const [animatedValues, setAnimatedValues] = useState(stats.map(() => 0));
  const [visibleCards, setVisibleCards] = useState(
    new Array(stats.length).fill(false)
  );

  // Animate bar fill
  useEffect(() => {
    let start = 0;
    const duration = 1500; // ms
    const stepTime = 20;

    const interval = setInterval(() => {
      start += stepTime;
      if (start >= duration) {
        setAnimatedValues(stats.map((stat) => stat.barValue));
        clearInterval(interval);
      } else {
        setAnimatedValues(
          stats.map((stat) =>
            Math.min(stat.barValue, Math.round((start / duration) * stat.barValue))
          )
        );
      }
    }, stepTime);

    return () => clearInterval(interval);
  }, []);

  // Animate cards entrance with stagger
  useEffect(() => {
    stats.forEach((_, i) => {
      setTimeout(() => {
        setVisibleCards((prev) => {
          const updated = [...prev];
          updated[i] = true;
          return updated;
        });
      }, i * 200); // 200ms delay between cards
    });
  }, []);

  return (
    <section className="py-16 px-6 md:px-20 bg-white">
      <div className="max-w-7xl mx-auto text-center mb-12">
        <h2 className="text-4xl font-bold mb-4">Why Choose Us?</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          We pride ourselves on excellence and commitment to providing the best
          service.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10 max-w-7xl mx-auto">
        {stats.map(({ id, icon, title, value, description, bgColor }, index) => (
          <div
            key={id}
            className={`flex flex-col items-center bg-gray-50 p-8 rounded-lg shadow-md cursor-default transform transition-all duration-700 ease-out
              ${
                visibleCards[index]
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }
              hover:shadow-xl
            `}
          >
            <div
              className={`flex items-center justify-center rounded-full w-16 h-16 mb-6 text-white bg-gradient-to-r ${bgColor}`}
            >
              {icon}
            </div>
            <h3 className="text-3xl font-extrabold mb-2 text-gray-900">{value}</h3>
            <h4 className="text-lg font-semibold mb-2">{title}</h4>
            <p className="text-gray-600 text-center">{description}</p>
          </div>
        ))}
      </div>

      {/* Animated Bar Graph */}
      <div className="max-w-4xl mx-auto mt-16">
        <h3 className="text-2xl font-semibold mb-6 text-center">
          Performance Overview
        </h3>
        <div className="space-y-6">
          {stats.map(({ id, title, bgColor }, index) => (
            <div key={id}>
              <div className="flex justify-between mb-1">
                <span className="text-gray-700 font-medium">{title}</span>
                <span className="text-gray-700 font-semibold">
                  {animatedValues[index]}%
                </span>
              </div>
              <div className="w-full bg-gray-300 rounded-full h-6 shadow-inner overflow-hidden">
                <div
                  className={`h-6 rounded-full bg-gradient-to-r ${bgColor} shadow-md transform transition-all duration-500 ease-out`}
                  style={{ width: `${animatedValues[index]}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AdvancedStats;
